import { useEffect, useMemo, useRef, useState } from 'react'
import { BbIcon } from './BbIcon'

/* A searchable single-select, for lists too long to scan in a native <select>.

   This is the plain sibling of BbFieldPicker: that one is built for the mapping
   grid and carries sections, "required" badges, conflict chips and per-field
   tooltips, none of which a list of account names has. The two share the
   .bb-pick-panel / -search / -list / -opt styling and nothing else.

   Use it where the option count makes searching worth an extra click — the
   chart of accounts runs to a hundred-odd entries. A short fixed list (item
   types, entity types) is better off as a native <select>, which keeps the
   platform's own keyboard and touch behaviour. */

export interface BbSelectOption {
  value: string
  label: string
}

export function BbSearchSelect({
  id,
  value,
  options,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  ariaLabel,
  disabled,
}: {
  id: string
  value: string
  options: BbSelectOption[]
  onChange: (next: string) => void
  /** Shown on the trigger while nothing is picked. */
  placeholder?: string
  searchPlaceholder?: string
  ariaLabel: string
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [up, setUp] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const search = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLUListElement>(null)

  const selected = options.find((option) => option.value === value) ?? null

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return options
    return options.filter((option) => option.label.toLowerCase().includes(needle))
  }, [options, query])

  /* The clear row sits at index 0 when it is offered, so every option's keyboard
     index is shifted by one. It is hidden while filtering: it would sit above
     the results for a search it has nothing to do with. */
  const showClear = !query.trim() && value !== ''
  const offset = showClear ? 1 : 0
  const rowCount = offset + matches.length

  /* Opening measures the trigger so a picker near the bottom of the window opens
     upward rather than off the page, and starts the highlight on the current
     value so Enter without typing is a no-op rather than a silent change. */
  useEffect(() => {
    if (!open) return
    const box = root.current?.getBoundingClientRect()
    // oxlint-disable-next-line react/set-state-in-effect
    if (box) setUp(window.innerHeight - box.bottom < 320)
    const current = options.findIndex((option) => option.value === value)
    // oxlint-disable-next-line react/set-state-in-effect
    setActive(current >= 0 ? current + (value !== '' ? 1 : 0) : 0)
    search.current?.focus()

    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open, options, value])

  // Keep the highlighted row in view when the keyboard walks past either edge.
  useEffect(() => {
    if (!open) return
    list.current?.querySelector('.is-active')?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  const choose = (next: string) => {
    onChange(next)
    setOpen(false)
    setQuery('')
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((index) => Math.min(rowCount - 1, Math.max(0, index + step)))
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      if (showClear && active === 0) choose('')
      else {
        const option = matches[active - offset]
        if (option) choose(option.value)
      }
      return
    }
    if (event.key === 'Escape' || event.key === 'Tab') setOpen(false)
  }

  return (
    <div className="bb-pick bb-sselect" ref={root}>
      <button
        className={selected ? 'bb-pick-btn' : 'bb-pick-btn bb-pick-off'}
        type="button"
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
      >
        {selected ? (
          <span className="bb-pick-label">{selected.label}</span>
        ) : (
          <span className="bb-sselect-ph">{placeholder}</span>
        )}
        <BbIcon name="chevron-down" />
      </button>

      {open ? (
        <div className={up ? 'bb-pick-panel up' : 'bb-pick-panel'}>
          <div className="bb-pick-search">
            <BbIcon name="search" />
            <input
              ref={search}
              type="search"
              autoComplete="off"
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                // The rows change under the cursor, so the highlight returns to
                // the top rather than pointing at whatever took that index.
                setActive(0)
              }}
              onKeyDown={onKeyDown}
            />
          </div>

          <ul className="bb-pick-list" role="listbox" aria-label={ariaLabel} ref={list}>
            {showClear ? (
              <li>
                <div
                  role="option"
                  aria-selected={false}
                  className={active === 0 ? 'bb-pick-opt is-active' : 'bb-pick-opt'}
                  onMouseEnter={() => setActive(0)}
                  onClick={() => choose('')}
                >
                  <span className="bb-pick-opt-label bb-pick-opt-muted">{placeholder}</span>
                </div>
              </li>
            ) : null}

            {matches.map((option, position) => {
              const index = offset + position
              return (
                // Position, not value: a caller whose options legitimately
                // repeat a value must not crash the list. Deduplicating is the
                // caller's decision, not this component's.
                <li key={`${option.value}-${position}`}>
                  <div
                    role="option"
                    aria-selected={option.value === value}
                    className={`bb-pick-opt${index === active ? ' is-active' : ''}${
                      option.value === value ? ' is-chosen' : ''
                    }`}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => choose(option.value)}
                  >
                    <span className="bb-pick-opt-label">{option.label}</span>
                  </div>
                </li>
              )
            })}
          </ul>

          {matches.length === 0 ? (
            <p className="bb-pick-empty">No match for “{query}”.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
